import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { map, type Observable, of } from 'rxjs';

import { environment } from '@env';

import { API_PATHS } from '@kernel';

import { HTTP_CONTEXT_SKIP_ERROR_TOAST } from '@core/http';

import {
  MOCK_BLOCKERS,
  MOCK_DOCUMENTS_REVIEW_COUNT,
  MOCK_FACILITY_CYCLES,
  MOCK_INVOICES,
} from './home.mock';
import {
  type Blocker,
  type BlockerApi,
  type FacilityCycle,
  type FacilityCycleApi,
  FORCE_EMPTY_HOME_DATA,
  type HomeRoleParam,
  type Invoice,
  type InvoiceApi,
} from './home.types';
import {
  mapBlockerApiToBlocker,
  mapFacilityCycleApiToFacilityCycle,
  mapInvoiceApiToInvoice,
} from './home.util';

@Injectable({ providedIn: 'root' })
export class HomeService {
  private readonly http = inject(HttpClient);

  getInvoices(role: HomeRoleParam): Observable<readonly Invoice[]> {
    if (FORCE_EMPTY_HOME_DATA) {
      return of([]);
    }
    if (environment.useMocks) return of(MOCK_INVOICES);

    const params = new HttpParams().set('role', role);

    return this.http
      .get<readonly InvoiceApi[]>(`${environment.apiUrl}${API_PATHS.invoices}`, {
        params,
        context: HTTP_CONTEXT_SKIP_ERROR_TOAST,
      })
      .pipe(map((rows) => (rows ?? []).map(mapInvoiceApiToInvoice)));
  }

  getBlockers(role: HomeRoleParam): Observable<readonly Blocker[]> {
    if (FORCE_EMPTY_HOME_DATA) {
      return of([]);
    }
    if (environment.useMocks) return of(MOCK_BLOCKERS);

    const params = new HttpParams().set('role', role);

    return this.http
      .get<readonly BlockerApi[]>(`${environment.apiUrl}${API_PATHS.blockers}`, {
        params,
        context: HTTP_CONTEXT_SKIP_ERROR_TOAST,
      })
      .pipe(map((rows) => (rows ?? []).map(mapBlockerApiToBlocker)));
  }

  getFacilityCycles(): Observable<readonly FacilityCycle[]> {
    if (FORCE_EMPTY_HOME_DATA) {
      return of([]);
    }
    if (environment.useMocks) return of(MOCK_FACILITY_CYCLES);

    return this.http
      .get<readonly FacilityCycleApi[]>(`${environment.apiUrl}${API_PATHS.facilityCycle}`, {
        context: HTTP_CONTEXT_SKIP_ERROR_TOAST,
      })
      .pipe(map((rows) => (rows ?? []).map(mapFacilityCycleApiToFacilityCycle)));
  }

  getDocumentsReviewCount(): Observable<number> {
    if (FORCE_EMPTY_HOME_DATA) {
      return of(0);
    }
    if (environment.useMocks) return of(MOCK_DOCUMENTS_REVIEW_COUNT);

    return this.http
      .get<number>(`${environment.apiUrl}${API_PATHS.documentsCount}`, {
        context: HTTP_CONTEXT_SKIP_ERROR_TOAST,
      })
      .pipe(map((count) => count ?? 0));
  }
}
